import { Fragment, useState } from 'react';
import { useTheme } from '../../../hooks/useTheme';
import { formatPrice } from '../../../hooks/useCurrency';
import {
  dentalCompare,
  dentalPlans,
  type DentalCell,
  type DentalCurrency,
} from '../../../mocks/dental';

interface DentalCompareProps {
  currency: DentalCurrency;
}

function CellValue({
  cell,
  accent,
  accentInk,
  isLight,
}: {
  cell: DentalCell;
  accent: string;
  accentInk: string;
  isLight: boolean;
}) {
  if (cell === 'check') {
    return (
      <span
        className="inline-flex w-5 h-5 items-center justify-center rounded-full"
        style={{ backgroundColor: accent + '22', color: isLight ? accentInk : accent }}
        title="Incluido"
      >
        <i className="ri-check-line text-xs font-bold" aria-hidden="true" />
        <span className="sr-only">Incluido</span>
      </span>
    );
  }
  if (cell === 'dash') {
    return (
      <span
        className="font-body text-sm"
        style={{ color: isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.45)' }}
      >
        <span aria-hidden="true">—</span>
        <span className="sr-only">No incluido</span>
      </span>
    );
  }
  return (
    <span
      className="font-body text-[11px] font-bold px-2 py-0.5 rounded-full inline-block"
      style={{ backgroundColor: accent + '18', color: isLight ? '#0a1628' : '#ffffff' }}
    >
      {cell}
    </span>
  );
}

export default function DentalCompare({ currency }: DentalCompareProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const accentText = isLight ? '#0f766e' : '#14b8a6';
  const [openCategory, setOpenCategory] = useState<string>(dentalCompare[0].id);
  /* La tabla es muy larga: se muestra solo cuando el visitante la pide. */
  const [showCompare, setShowCompare] = useState(false);

  const ink = isLight ? '#0a1628' : '#ffffff';
  const sub = isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.55)';
  const border = isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.08)';
  const rowBorder = isLight ? 'rgba(10,22,40,0.07)' : 'rgba(255,255,255,0.06)';
  const headerBg = isLight ? '#eef4f9' : 'rgba(7,27,46,0.95)';
  const labelBg = isLight ? '#f6fafd' : 'rgba(7,27,46,0.45)';

  return (
    <section
      id="comparativa-odontologia"
      className="py-14 md:py-20 px-4 sm:px-6 scroll-mt-24"
      style={{
        background: isLight
          ? 'linear-gradient(180deg, #ffffff 0%, #f6fafd 100%)'
          : 'linear-gradient(180deg, #04101f 0%, #071b2e 100%)',
        borderTop: `1px solid ${border}`,
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: ink }}>
            Comparativa <span style={{ color: accentText }}>completa</span>
          </h2>
          <p className="font-body text-sm md:text-base max-w-2xl mx-auto mb-6" style={{ color: sub }}>
            Todo lo que incluye cada plan, categoría por categoría.
          </p>

          {/* Interruptor: la comparativa completa está plegada por defecto */}
          <button
            type="button"
            onClick={() => setShowCompare((v) => !v)}
            aria-expanded={showCompare}
            aria-controls="comparativa-odontologia-panel"
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-body text-sm font-semibold cursor-pointer transition-all duration-200 hover:scale-[1.02]"
            style={{
              borderColor: isLight ? 'rgba(6,182,212,0.40)' : 'rgba(6,182,212,0.30)',
              backgroundColor: isLight ? 'rgba(6,182,212,0.08)' : 'rgba(6,182,212,0.10)',
              color: isLight ? '#0e7490' : '#22d3ee',
            }}
          >
            <i className="ri-table-line text-base" aria-hidden="true" />
            {showCompare ? 'Ocultar comparativa completa' : 'Ver comparativa completa'}
            <i
              className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${showCompare ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>

        <div id="comparativa-odontologia-panel" hidden={!showCompare}>
        {/* ── Desktop: tabla ── */}
        <div
          className="hidden lg:block dental-scroll-x rounded-2xl border"
          style={{ borderColor: border }}
        >
          <table className="w-full min-w-[860px]" style={{ borderCollapse: 'collapse' }}>
            <caption className="sr-only">
              Comparativa de funciones de los planes de InnoFlex Odontología
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="text-left p-4 pl-5 font-body text-xs font-semibold uppercase tracking-wider w-[30%]"
                  style={{ backgroundColor: headerBg, color: sub }}
                >
                  Función
                </th>
                {dentalPlans.map((plan) => (
                  <th
                    key={plan.id}
                    scope="col"
                    className="p-4 text-center"
                    style={{
                      backgroundColor: plan.badge
                        ? plan.accent + (isLight ? '16' : '14')
                        : headerBg,
                      borderLeft: `1px solid ${rowBorder}`,
                      minWidth: 120,
                    }}
                  >
                    <div
                      className="font-heading text-sm md:text-base font-extrabold"
                      style={{ color: isLight ? plan.accentInk : plan.accent }}
                    >
                      {plan.name}
                    </div>
                    <div className="font-heading text-xs font-bold mt-1" style={{ color: ink }}>
                      {plan.from ? 'Desde ' : ''}
                      {formatPrice(plan.monthly[currency], currency)}
                      <span className="font-body font-normal" style={{ color: sub }}>
                        /mes
                      </span>
                    </div>
                    {plan.badge && (
                      <div
                        className="font-body text-[10px] font-bold mt-1"
                        style={{ color: isLight ? plan.accentInk : plan.accent }}
                      >
                        {plan.badge}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dentalCompare.map((category) => (
                <Fragment key={category.id}>
                  <tr>
                    <th
                      scope="colgroup"
                      colSpan={dentalPlans.length + 1}
                      className="text-left px-5 py-2.5 font-body text-[11px] font-bold uppercase tracking-wider"
                      style={{
                        backgroundColor: isLight ? '#e6eef6' : 'rgba(6,182,212,0.10)',
                        color: isLight ? '#0e7490' : '#22d3ee',
                        borderTop: `1px solid ${rowBorder}`,
                      }}
                    >
                      <i className={`${category.icon} text-xs mr-1.5`} aria-hidden="true" />
                      {category.title}
                    </th>
                  </tr>
                  {category.rows.map((row, i) => (
                    <tr key={`${category.id}-${row.label}`} style={{ borderTop: `1px solid ${rowBorder}` }}>
                      <th
                        scope="row"
                        className="text-left p-3.5 pl-5 font-body text-xs font-normal"
                        style={{ backgroundColor: labelBg, color: sub }}
                      >
                        {row.label}
                      </th>
                      {row.values.map((cell, idx) => (
                        <td
                          key={dentalPlans[idx].id}
                          className="p-3.5 text-center"
                          style={{
                            borderLeft: `1px solid ${rowBorder}`,
                            backgroundColor:
                              i % 2 === 0
                                ? isLight
                                  ? 'rgba(10,22,40,0.012)'
                                  : 'rgba(255,255,255,0.012)'
                                : 'transparent',
                          }}
                        >
                          <CellValue cell={cell} accent={dentalPlans[idx].accent} accentInk={dentalPlans[idx].accentInk} isLight={isLight} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Móvil y tablet: acordeones ── */}
        <div className="lg:hidden flex flex-col gap-2.5">
          {dentalCompare.map((category) => {
            const open = openCategory === category.id;
            return (
              <div
                key={category.id}
                className="rounded-xl border overflow-hidden"
                style={{
                  borderColor: open ? 'rgba(6,182,212,0.35)' : border,
                  background: isLight ? '#ffffff' : 'rgba(7,27,46,0.75)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenCategory(open ? '' : category.id)}
                  aria-expanded={open}
                  aria-controls={`panel-${category.id}`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left cursor-pointer"
                  style={{ backgroundColor: open ? 'rgba(6,182,212,0.08)' : 'transparent' }}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <i
                      className={`${category.icon} text-base flex-shrink-0`}
                      style={{ color: '#14b8a6' }}
                      aria-hidden="true"
                    />
                    <span
                      className="font-heading text-sm font-bold truncate"
                      style={{ color: open ? (isLight ? '#0e7490' : '#22d3ee') : ink }}
                    >
                      {category.title}
                    </span>
                  </span>
                  <i
                    className="ri-arrow-down-s-line text-lg flex-shrink-0 transition-transform duration-200"
                    style={{
                      color: '#06b6d4',
                      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                    aria-hidden="true"
                  />
                </button>

                {open && (
                  <div
                    id={`panel-${category.id}`}
                    className="px-4 pb-4 pt-1"
                    style={{ borderTop: `1px solid ${rowBorder}` }}
                  >
                    {category.rows.map((row) => (
                      <div key={row.label} className="py-2.5" style={{ borderBottom: `1px solid ${rowBorder}` }}>
                        <p className="font-body text-xs mb-2" style={{ color: ink }}>
                          {row.label}
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          {row.values.map((cell, idx) => {
                            const plan = dentalPlans[idx];
                            return (
                              <div
                                key={plan.id}
                                className="flex flex-col items-center gap-1 rounded-lg py-1.5"
                                style={{ backgroundColor: plan.accent + (isLight ? '0d' : '12') }}
                              >
                                <span
                                  className="font-body text-[9px] font-bold uppercase tracking-wide"
                                  style={{ color: isLight ? plan.accentInk : plan.accent }}
                                >
                                  {plan.name}
                                </span>
                                <CellValue cell={cell} accent={plan.accent} accentInk={plan.accentInk} isLight={isLight} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Leyenda */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-flex w-4 h-4 items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(20,184,166,0.22)', color: '#14b8a6' }}
            >
              <i className="ri-check-line text-[10px] font-bold" aria-hidden="true" />
            </span>
            <span className="font-body text-xs" style={{ color: sub }}>
              Incluido
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="font-body text-sm" style={{ color: sub }}>
              —
            </span>
            <span className="font-body text-xs" style={{ color: sub }}>
              No incluido en ese plan
            </span>
          </span>
        </div>
        </div>
      </div>
    </section>
  );
}
